import { useRolodexEdit } from 'features/fde'
import { useFeatureInteractionContext } from 'features/siteMap/contexts/FeatureInteractionContext'
import { useMapVizCameraContext } from 'features/siteMap/contexts/MapVizCameraContext'
import { useSiteModelContext } from 'features/siteMap/contexts/SiteModelContext'
import { useTilesContext } from 'features/siteMap/contexts/TilesContext'
import { useCallback, useMemo } from 'react'
import { Matrix4, Vector3 } from 'three'
import { degToRad, radToDeg } from 'three/src/math/MathUtils'
import {
  SiteFeaturePrimitive,
  SiteFeaturePrimitiveBox,
  SiteFeaturePrimitiveCylinder,
  SiteFeaturePrimitiveKind,
  SiteFeaturePrimitiveSphere,
  SiteModelCoordinate,
} from 'types'

export function useTilesetMapFeatureHelpers() {
  const { siteViewFeatureGenerationConfig, getHasDetailedModel } =
    useFeatureInteractionContext()
  const { camera } = useMapVizCameraContext()
  const { tilesEllipsoid, tilesMatrix } = useTilesContext()
  const { siteModel, setSiteModel } = useSiteModelContext()

  const { rolodexPatch } = useRolodexEdit()
  const rolodexPatchFailOnAuthError = useCallback(
    (entryPathJSON: Parameters<typeof rolodexPatch>[0]) => {
      rolodexPatch(entryPathJSON).then((res) => {
        if (res.status === 403) {
          throw new Error(
            'Failed to patch site model due to authentication error.'
          )
        }
      })
    },
    [rolodexPatch]
  )

  const convertPositionToCartographic = (
    position: Vector3
  ): SiteModelCoordinate | undefined => {
    const invertedPos = new Vector3()
    invertedPos.copy(position)
    const matrix = new Matrix4()
    const invMatrix = matrix.copy(tilesMatrix).invert()
    invertedPos.applyMatrix4(invMatrix)

    const coord = { lat: 0, lon: 0, height: 0 }
    tilesEllipsoid.getPositionToCartographic(invertedPos, coord)
    coord.lat = radToDeg(coord.lat)
    coord.lon = radToDeg(coord.lon)

    return coord
  }

  const convertCartographicToPosition = useCallback(
    (position: SiteModelCoordinate): Vector3 => {
      const matrix = new Matrix4()
      matrix.copy(tilesMatrix)

      const xyzs = new Vector3()
      tilesEllipsoid
        .getCartographicToPosition(
          degToRad(position.lat),
          degToRad(position.lon),
          position.height,
          xyzs
        )
        .applyMatrix4(matrix)
      return xyzs
    },
    [tilesEllipsoid, tilesMatrix]
  )

  const lookAtFeature = useCallback(
    (feature: { coordinate: SiteModelCoordinate }) => {
      if (camera?.current) {
        camera.current.lookAt(
          convertCartographicToPosition({
            lat: feature.coordinate.lat,
            lon: feature.coordinate.lon,
            height: feature.coordinate.height,
          })
        )
      }
    },
    [camera, convertCartographicToPosition]
  )

  const moveToFeature = useCallback(
    (feature: { coordinate: SiteModelCoordinate }) => {
      if (camera?.current) {
        const positionToMoveTo = convertCartographicToPosition({
          lat: feature.coordinate.lat,
          lon: feature.coordinate.lon,
          height: feature.coordinate.height,
        })
        camera.current.position.x = positionToMoveTo.x
        camera.current.position.y = positionToMoveTo.y
        camera.current.position.z = positionToMoveTo.z
      }
    },
    [camera, convertCartographicToPosition]
  )

  const debounceMap = useMemo(() => new Map<string, NodeJS.Timeout>(), [])
  const updateFeaturePrimitive = (
    feature: SiteFeaturePrimitive,
    debounceFieldKey?: string | string[]
  ) => {
    const newSiteModel = { ...siteModel }
    newSiteModel.data.features[feature.id] = feature
    setSiteModel(newSiteModel)

    const featureData = [
      {
        id: newSiteModel.id,
        data: {
          features: {
            [feature.id]: feature,
          },
        },
      },
    ]

    if (debounceFieldKey) {
      const key =
        feature.id +
        '__' +
        (Array.isArray(debounceFieldKey)
          ? (debounceFieldKey as string[]).join('_')
          : (debounceFieldKey as string))
      clearTimeout(debounceMap.get(key))
      debounceMap.set(
        key,
        setTimeout(() => {
          rolodexPatchFailOnAuthError(featureData)
        }, 250)
      )
    } else {
      rolodexPatchFailOnAuthError(featureData)
    }
  }

  const convertShapeDimensions = (
    feature: SiteFeaturePrimitive,
    oldKind: SiteFeaturePrimitiveKind,
    newKind: SiteFeaturePrimitiveKind
  ) => {
    const newDimensions = new Vector3(0, 0, 0)
    if (oldKind === SiteFeaturePrimitiveKind.Sphere) {
      const sphere = feature as SiteFeaturePrimitiveSphere
      newDimensions.x = sphere.geometry.radius
      newDimensions.y = sphere.geometry.radius
      newDimensions.z = sphere.geometry.radius
    } else if (oldKind === SiteFeaturePrimitiveKind.Cylinder) {
      const cylinder = feature as SiteFeaturePrimitiveCylinder
      newDimensions.x = cylinder.geometry.bottom_radius
      newDimensions.y = cylinder.geometry.bottom_radius
      newDimensions.z = cylinder.geometry.height
    } else if (oldKind === SiteFeaturePrimitiveKind.Box) {
      const box = feature as SiteFeaturePrimitiveBox
      newDimensions.x = box.geometry.width
      newDimensions.y = box.geometry.depth
      newDimensions.z = box.geometry.height
    }

    if (newKind === SiteFeaturePrimitiveKind.Sphere) {
      feature.geometry = {
        ...feature.geometry,
        radius: newDimensions.x,
      }
    } else if (newKind === SiteFeaturePrimitiveKind.Cylinder) {
      feature.geometry = {
        ...feature.geometry,
        bottom_radius: newDimensions.x,
        top_radius: newDimensions.x,
        height: newDimensions.z,
      }
    } else if (newKind === SiteFeaturePrimitiveKind.Box) {
      feature.geometry = {
        ...feature.geometry,
        width: newDimensions.x,
        depth: newDimensions.y,
        height: newDimensions.z,
      }
    }

    return feature
  }

  const getFeatureOpacity = (feature: SiteFeaturePrimitive) => {
    const hasDetailedModel = feature.unit_id
      ? getHasDetailedModel(feature.unit_id)
      : false

    if (hasDetailedModel) {
      return 0.4
    }
    return siteViewFeatureGenerationConfig?.primitive.id === feature.id
      ? 0.25
      : 0.7
  }

  return {
    convertPositionToCartographic,
    convertCartographicToPosition,
    lookAtFeature,
    moveToFeature,
    updateFeaturePrimitive,
    convertShapeDimensions,
    getFeatureOpacity,
  }
}
